import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    LuMail,
    LuBriefcase,
    LuMessageCircle,
    LuChartPie,
    LuFileText,
    LuShoppingBag,
    LuGlobe,
    LuClock,
    LuShield,
    LuBot
} from 'react-icons/lu';

const AutonomousAgents = () => {
    const [hoveredRole, setHoveredRole] = useState<number | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div>
            {/* Hero Section */}
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-primary py-20 lg:py-32">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center space-x-2 text-blue-100 font-bold tracking-wider text-sm mb-6 uppercase">
                        <span>The Future Workforce</span>
                    </div>
                    <h1 className="text-5xl lg:text-7xl font-bold text-white tracking-tight mb-6">
                        Deploy Your <span className="text-secondary">24/7 AI Team</span>
                    </h1>
                    <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-10 leading-relaxed">
                        Empowering Indian enterprises with autonomous intelligent agents that handle complex workflows from Sales to HR—nonstop, error-free, and scalable.
                    </p>
                </div>
            </section>

            {/* Specialized Agent Roles Section */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Specialized Agent Roles</h2>
                        <p className="text-xl text-slate-600">
                            Select the right digital workers for your enterprise needs.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: LuMail,
                                title: "AI Lead Management Agent",
                                desc: "Auto-capture, qualify, assign, and follow up leads instantly using multi-channel intelligence."
                            },
                            {
                                icon: LuBriefcase,
                                title: "Sales & CRM Automation Agent",
                                desc: "Update CRM, trigger reminders, send proposals, and notify sales teams automatically."
                            },
                            {
                                icon: LuMessageCircle,
                                title: "AI WhatsApp & Chatbot Agent",
                                desc: "Respond, book appointments, and sync data automatically across WhatsApp and Web Chat."
                            },
                            {
                                icon: LuChartPie,
                                title: "Reporting & Analytics Agent",
                                desc: "Auto-generate daily/weekly business reports and deliver actionable insights to stakeholders."
                            },
                            {
                                icon: LuFileText,
                                title: "HR & Admin Automation Agent",
                                desc: "Resume screening, onboarding workflows, document handling, and employee query resolution."
                            },
                            {
                                icon: LuShoppingBag,
                                title: "E-commerce AI Agent",
                                desc: "Order updates, inventory sync, customer notifications, and personalized product recommendations."
                            }
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="relative overflow-hidden bg-white rounded-[2rem] p-10 border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group text-center flex flex-col items-center"
                                onMouseEnter={() => setHoveredRole(index)}
                                onMouseLeave={() => setHoveredRole(null)}
                                style={{
                                    backgroundImage: hoveredRole === index
                                        ? `linear-gradient(to bottom right, #2563eb0d, transparent)`
                                        : undefined
                                }}
                            >
                                <div className="relative z-10 w-full flex flex-col items-center">
                                    <div className="w-16 h-16 bg-blue-100/50 rounded-full flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform duration-300"
                                        style={{ boxShadow: `0 10px 15px -3px #2563eb33` }}>
                                        {React.createElement(item.icon as any, { className: "w-7 h-7" })}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-3 transition-colors duration-300"
                                        style={{ color: hoveredRole === index ? '#2563eb' : undefined }}>
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-600 leading-relaxed text-sm">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Dubai & GCC Focus Section */}
            <section className="py-24 bg-slate-50 border-t border-slate-200">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-4 uppercase">
                            <span>India Focus</span>
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900">Optimized for Region</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-12 text-center">
                        {[
                            {
                                icon: LuGlobe,
                                title: "Multilingual Support",
                                desc: "Support for Indian languages & English ensuring seamless communication across India."
                            },
                            {
                                icon: LuClock,
                                title: "Timezone Aligned",
                                desc: "Agents operate 24/7 but are optimized for local business hours handoffs."
                            },
                            {
                                icon: LuShield,
                                title: "Data Sovereignty",
                                desc: "Compliant with local data protection regulations for secure enterprise deployment."
                            }
                        ].map((item, index) => (
                            <div key={index} className="flex flex-col items-center">
                                <div className="w-20 h-20 bg-white border border-slate-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
                                    {React.createElement(item.icon as any, { className: "w-8 h-8 text-blue-600" })}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600 max-w-xs mx-auto text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            {/* CTA Section */}
            <section className="py-14 bg-white border-t border-gray-100 mt-10">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
                        Ready to Automate Your Operations?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Hire your first digital worker today. Transform efficiency and reduce costs by up to 30%.
                    </p>
                    <div className="flex justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-8 py-4 bg-[#0B25E6] text-white rounded-lg hover:bg-blue-800  transition-colors text-lg font-bold shadow-lg gap-2"
                        >
                            Deploy Your AI Agent
                            {React.createElement(LuBot as any, { className: "w-5 h-5" })}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AutonomousAgents;
